import type { Coordinate } from '@studio/js-utils/geometry';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { FastFrameSelectStage } from './types.js';
import type { Tool } from '#~/control/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { depot } from '#~/depot/index.js';

/**
 * Set fast frame initial scale.
 *
 * @param scale - The fast frame initial scale.
 */
export const setFastFrameInitialScale = (scale: number): void => {
  const state = depot.getState();

  state.produce((draft) => {
    draft.toolStates.fastFrameInitialScale = scale;
  });
};

/**
 * Set fast frame on exit tool.
 *
 * @param tool - The fast frame on exit tool.
 */
export const setFastFrameOnExitTool = (tool: T_Maybe<Tool>): void => {
  const state = depot.getState();

  state.produce((draft) => {
    draft.toolStates.fastFrameOnExitTool = tool;
  });
};

/**
 * Set fast frame select stage.
 *
 * @param stage - The fast frame select stage.
 */
export const setFastFrameSelectStage = (stage: FastFrameSelectStage): void => {
  const state = depot.getState();

  state.produce((draft) => {
    draft.toolStates.fastFrameSelectStage = stage;
  });
};

/**
 * Set fast frame initial position.
 *
 * @param position - The fast frame initial position.
 */
export const setToolInitialPosition = (position: T_Maybe<Coordinate>): void => {
  const state = depot.getState();

  state.produce((draft) => {
    draft.toolStates.toolInitialPosition = position;
  });
};

/**
 * Fast frame initial scale context.
 *
 * @returns The fast frame initial scale.
 */
export const useFastFrameInitialScale = (): number =>
  useDepot((µ) => µ.toolStates.fastFrameInitialScale);

/**
 * Fast frame on exit tool context.
 *
 * @returns The fast frame on exit tool.
 */
export const useFastFrameOnExitTool = (): T_Maybe<Tool> =>
  useDepot((µ) => µ.toolStates.fastFrameOnExitTool);

/**
 * Fast frame select stage context.
 *
 * @returns The fast frame select stage.
 */
export const useFastFrameSelectStage = (): FastFrameSelectStage =>
  useDepot((µ) => µ.toolStates.fastFrameSelectStage);

/**
 * Fast frame initial position context.
 *
 * @returns The fast frame initial position.
 */
export const useToolInitialPosition = (): T_Maybe<Coordinate> =>
  useDepot((µ) => µ.toolStates.toolInitialPosition);
